import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Player } from '@/types/player';
import { fetchPlayersData, addPlayerData, updatePlayerData } from '@/services/playerService';

export type { Player } from '@/types/player';

export const usePlayers = () => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const { profile } = useAuth();

  const fetchPlayers = useCallback(async () => {
    if (!profile) return;

    try {
      setLoading(true);
      const playersData = await fetchPlayersData(profile);
      setPlayers(playersData);
    } catch (error) {
      console.error('Error fetching players:', error);
      toast.error('Failed to load players');
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    if (profile) {
      fetchPlayers();
    }
  }, [profile, fetchPlayers]);

  const addPlayer = async (playerData: Omit<Player, 'id' | 'profile'>) => {
    try {
      const newPlayer = await addPlayerData(playerData);
      setPlayers(prev => [...prev, newPlayer]);
      toast.success('Player added successfully');
      return newPlayer;
    } catch (error) {
      console.error('Error adding player:', error);
      toast.error('Failed to add player');
      return null;
    }
  };

  const updatePlayer = async (playerId: string, updates: Partial<Player>) => {
    try {
      const updatedPlayer = await updatePlayerData(playerId, updates);
      setPlayers(prev => prev.map(p => p.id === playerId ? updatedPlayer : p));
      toast.success('Player updated successfully');
      return updatedPlayer;
    } catch (error) {
      console.error('Error updating player:', error);
      toast.error('Failed to update player');
      return null;
    }
  };

  return {
    players,
    loading,
    fetchPlayers,
    addPlayer,
    updatePlayer
  };
};
